import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, LogOut, Search, RefreshCw, Inbox, Check, Undo2, Trash2, Bell, Images, KeyRound } from "lucide-react";
import { GalleryAdmin } from "./GalleryAdmin";
import { ChangePasswordDialog } from "./AdminPassword";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "zts-admin-token";
const DAY_MS = 24 * 60 * 60 * 1000;

const fmtDate = (iso) => new Date(iso).toLocaleString("sk-SK", { dateStyle: "medium", timeStyle: "short" });
const isRecent = (iso) => Date.now() - new Date(iso).getTime() < DAY_MS;

const AdminLogin = ({ onToken }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotState, setForgotState] = useState("idle");
  const [forgotMsg, setForgotMsg] = useState("");

  const forgot = async () => {
    setForgotState("sending");
    setForgotMsg("");
    try {
      const res = await fetch(`${API}/api/admin/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin: window.location.origin }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Odoslanie zlyhalo.");
      setForgotState("sent");
      setForgotMsg("Odkaz na obnovu hesla sme poslali na firemný e-mail. Platí 60 minút.");
    } catch (err) {
      setForgotState("error");
      setForgotMsg(err.message);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setForgotMsg("");
    try {
      const res = await fetch(`${API}/api/admin/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) throw new Error("Nesprávne heslo.");
      const data = await res.json();
      sessionStorage.setItem(TOKEN_KEY, data.access_token);
      onToken(data.access_token);
    } catch (err) {
      setError(err.message || "Prihlásenie zlyhalo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink grid place-items-center px-4">
      <form onSubmit={submit} data-testid="admin-login-form"
        className="w-full max-w-sm border border-steelborder bg-steelcard p-8 space-y-6">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-9 h-9 bg-safety text-ink"><Lock className="w-4 h-4" /></span>
          <div>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Interná sekcia</p>
            <h1 className="font-heading font-bold text-2xl uppercase text-slate-100">Prehľad dopytov</h1>
          </div>
        </div>
        <label className="block space-y-2">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400">Heslo</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus
            data-testid="admin-password-input"
            className="w-full bg-gunmetal border border-steelborder px-4 py-3 text-slate-100 font-body focus:outline-none focus:border-safety transition-colors" />
        </label>
        {error && <p data-testid="admin-login-error" className="font-mono text-xs text-safety">{error}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-submit"
          className="w-full bg-safety hover:bg-safety-hover disabled:opacity-60 text-ink font-heading font-bold uppercase tracking-wider py-3 transition-colors">
          {loading ? "Overujem..." : "Vstúpiť"}
        </button>
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="admin-back-home" className="font-mono text-xs text-slate-500 hover:text-slate-200 transition-colors">← Späť na web</Link>
          <button type="button" onClick={forgot} disabled={forgotState === "sending"} data-testid="admin-forgot-button"
            className="font-mono text-xs text-slate-500 hover:text-safety transition-colors disabled:opacity-60">
            {forgotState === "sending" ? "Odosielam..." : "Zabudli ste heslo?"}
          </button>
        </div>
        {forgotMsg && <p data-testid="admin-forgot-message" className={`font-mono text-xs ${forgotState === "sent" ? "text-emerald-400" : "text-safety"}`}>{forgotMsg}</p>}
      </form>
    </div>
  );
};

const EnquiryRow = ({ e, i, onStatus, onDelete }) => {
  const [confirming, setConfirming] = useState(false);
  const done = e.status === "done";
  const recent = isRecent(e.created_at);
  return (
    <tr data-testid={`enquiry-row-${i}`} data-status={e.status} data-recent={recent}
      className={`border-t border-steelborder align-top transition-colors ${done ? "opacity-50 hover:opacity-80" : "hover:bg-gunmetal/60"} ${recent && !done ? "bg-safety/[0.06] border-l-2 border-l-safety" : "border-l-2 border-l-transparent"}`}>
      <td className="px-4 py-4 whitespace-nowrap">
        <div className="font-mono text-xs text-slate-500">{fmtDate(e.created_at)}</div>
        {recent && !done && (
          <span data-testid={`enquiry-recent-badge-${i}`} className="inline-flex items-center gap-1.5 mt-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-safety">
            <span className="w-1.5 h-1.5 rounded-full bg-safety animate-pulse-dot" /> posledných 24 h
          </span>
        )}
      </td>
      <td className="px-4 py-4 whitespace-nowrap">
        <span data-testid={`enquiry-status-${i}`}
          className={`inline-block font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${done ? "border-emerald-500/50 text-emerald-400" : "border-safety text-safety"}`}>
          {done ? "Vybavený" : "Nový"}
        </span>
      </td>
      <td className="px-4 py-4 text-slate-100 font-semibold whitespace-nowrap">{e.name}</td>
      <td className="px-4 py-4 whitespace-nowrap">
        <a href={`mailto:${e.email}`} className="text-blueprint hover:underline">{e.email}</a>
        {e.phone && <div className="font-mono text-xs text-slate-400 mt-1">{e.phone}</div>}
      </td>
      <td className="px-4 py-4 text-slate-300 text-sm whitespace-pre-wrap max-w-xl">{e.message}</td>
      <td className="px-4 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2 justify-end">
          <button onClick={() => onStatus(e.id, done ? "new" : "done")} data-testid={`enquiry-toggle-status-${i}`}
            title={done ? "Vrátiť medzi nové" : "Označiť ako vybavený"}
            className={`flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1.5 border transition-colors ${done ? "border-steelborder text-slate-400 hover:text-slate-100 hover:border-slate-400" : "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500 hover:text-ink"}`}>
            {done ? <><Undo2 className="w-3.5 h-3.5" /> Vrátiť</> : <><Check className="w-3.5 h-3.5" /> Vybavené</>}
          </button>
          {confirming ? (
            <span className="flex items-center gap-1" data-testid={`enquiry-delete-confirm-${i}`}>
              <button onClick={() => onDelete(e.id)} data-testid={`enquiry-delete-yes-${i}`}
                className="font-mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1.5 bg-red-600 text-white hover:bg-red-500 transition-colors">Vymazať</button>
              <button onClick={() => setConfirming(false)} data-testid={`enquiry-delete-no-${i}`}
                className="font-mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1.5 border border-steelborder text-slate-400 hover:text-slate-100 transition-colors">Zrušiť</button>
            </span>
          ) : (
            <button onClick={() => setConfirming(true)} data-testid={`enquiry-delete-${i}`} title="Vymazať dopyt"
              className="p-1.5 border border-steelborder text-slate-500 hover:text-red-400 hover:border-red-400 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const FILTERS = [
  { key: "all", label: "Všetky" },
  { key: "new", label: "Nové" },
  { key: "done", label: "Vybavené" },
];

const EnquiryTable = ({ authed, error, setError }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await authed("/enquiries");
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await authed(`/enquiries/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
      setItems((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
    } catch (err) { setError(err.message); }
  };

  const remove = async (id) => {
    try {
      await authed(`/enquiries/${id}`, { method: "DELETE" });
      setItems((prev) => prev.filter((e) => e.id !== id));
      setTotal((t) => t - 1);
    } catch (err) { setError(err.message); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const needle = q.trim().toLowerCase();
  const counts = { all: items.length, new: items.filter((e) => e.status !== "done").length, done: items.filter((e) => e.status === "done").length };
  const recentCount = items.filter((e) => e.status !== "done" && isRecent(e.created_at)).length;
  const filtered = items
    .filter((e) => filter === "all" || (filter === "done" ? e.status === "done" : e.status !== "done"))
    .filter((e) => !needle || [e.name, e.email, e.phone, e.message].some((v) => v?.toLowerCase().includes(needle)));

  return (
    <>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-safety">Dopyty z webu</p>
            <h1 className="font-heading font-bold text-4xl uppercase mt-1">
              Prijaté dopyty <span data-testid="enquiry-total" className="text-slate-500 font-mono text-2xl">/ {total}</span>
            </h1>
          </div>
          <div className="flex gap-2">
            <label className="flex items-center gap-2 border border-steelborder bg-gunmetal px-3">
              <Search className="w-4 h-4 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hľadať meno, e-mail, text..."
                data-testid="enquiry-search-input"
                className="bg-transparent py-2.5 w-64 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none" />
            </label>
            <button onClick={load} data-testid="enquiry-refresh-button" title="Obnoviť"
              className="border border-steelborder bg-gunmetal px-3 text-slate-400 hover:text-safety hover:border-safety transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {error && <p data-testid="enquiry-error" className="font-mono text-xs text-safety">{error}</p>}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex gap-1" data-testid="enquiry-filters">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`enquiry-filter-${f.key}`}
                className={`font-mono text-xs tracking-[0.15em] uppercase px-3 py-2 border transition-colors ${filter === f.key ? "border-safety text-safety bg-safety/10" : "border-steelborder text-slate-400 hover:text-slate-100"}`}>
                {f.label} <span className="text-slate-500">{counts[f.key]}</span>
              </button>
            ))}
          </div>
          {recentCount > 0 && (
            <div data-testid="enquiry-recent-alert" className="flex items-center gap-2 border border-safety/60 bg-safety/10 px-3 py-2 font-mono text-xs text-safety">
              <Bell className="w-4 h-4" />
              <span><strong>{recentCount}</strong> {recentCount === 1 ? "nový dopyt" : recentCount < 5 ? "nové dopyty" : "nových dopytov"} za posledných 24 h</span>
            </div>
          )}
        </div>

        <div className="border border-steelborder bg-steelcard overflow-x-auto">
          <table className="w-full text-left" data-testid="enquiry-table">
            <thead>
              <tr className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">
                <th className="px-4 py-3">Dátum</th>
                <th className="px-4 py-3">Stav</th>
                <th className="px-4 py-3">Meno</th>
                <th className="px-4 py-3">Kontakt</th>
                <th className="px-4 py-3">Správa</th>
                <th className="px-4 py-3 text-right">Akcie</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e, i) => <EnquiryRow key={e.id} e={e} i={i} onStatus={setStatus} onDelete={remove} />)}
            </tbody>
          </table>
          {!loading && filtered.length === 0 && (
            <div data-testid="enquiry-empty" className="flex flex-col items-center gap-3 py-16 text-slate-500">
              <Inbox className="w-8 h-8" />
              <p className="font-mono text-xs tracking-[0.2em] uppercase">{needle || filter !== "all" ? "Žiadne výsledky" : "Zatiaľ žiadne dopyty"}</p>
            </div>
          )}
        </div>
    </>
  );
};

const TABS = [
  { key: "enquiries", label: "Dopyty", icon: Inbox },
  { key: "gallery", label: "Galéria", icon: Images },
];

const AdminShell = ({ token, onLogout }) => {
  const [tab, setTab] = useState("enquiries");
  const [error, setError] = useState("");
  const [pwOpen, setPwOpen] = useState(false);

  const authed = async (path, opts = {}, isForm = false) => {
    const headers = { Authorization: `Bearer ${token}`, ...(isForm ? {} : { "Content-Type": "application/json" }), ...(opts.headers || {}) };
    const res = await fetch(`${API}/api/admin${path}`, { ...opts, headers });
    if (res.status === 401) { onLogout(); throw new Error("Relácia vypršala."); }
    if (!res.ok) {
      const detail = await res.json().then((d) => d.detail).catch(() => null);
      throw new Error(typeof detail === "string" ? detail : "Operácia zlyhala.");
    }
    return res.json();
  };

  return (
    <div className="min-h-screen bg-ink text-slate-100">
      <header className="border-b border-steelborder bg-ink/85 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="admin-brand-link" className="flex items-center">
            <img src="/brand/logo-dark.png" alt="ZTS F-Invest s.r.o." className="h-10 w-auto" />
          </Link>
          <nav className="flex items-center gap-1" data-testid="admin-tabs">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => { setTab(t.key); setError(""); }} data-testid={`admin-tab-${t.key}`}
                className={`flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase px-3 py-2 border-b-2 transition-colors ${tab === t.key ? "border-safety text-safety" : "border-transparent text-slate-400 hover:text-slate-100"}`}>
                <t.icon className="w-4 h-4" /> <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <button onClick={() => setPwOpen(true)} data-testid="admin-change-password-button" title="Zmeniť heslo"
              className="flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-safety transition-colors">
              <KeyRound className="w-4 h-4" /> <span className="hidden md:inline">Heslo</span>
            </button>
            <button onClick={onLogout} data-testid="admin-logout-button"
              className="flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-safety transition-colors">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Odhlásiť</span>
            </button>
          </div>
        </div>
      </header>
      {pwOpen && <ChangePasswordDialog authed={authed} onClose={() => setPwOpen(false)} />}

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-6">
        {tab === "enquiries" ? (
          <EnquiryTable authed={authed} error={error} setError={setError} />
        ) : (
          <>
            <div>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-safety">Fotografie na webe</p>
              <h1 className="font-heading font-bold text-4xl uppercase mt-1">Fotogaléria</h1>
            </div>
            {error && <p data-testid="gallery-error" className="font-mono text-xs text-safety">{error}</p>}
            <GalleryAdmin authed={authed} setError={setError} />
          </>
        )}
      </main>
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    document.title = "Administrácia | ZTS F-Invest";
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return token ? <AdminShell token={token} onLogout={logout} /> : <AdminLogin onToken={setToken} />;
}
